import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import {
  buildLiquidationDocumentHtml,
  buildRequestVoucherHtml,
} from "../../utils/costAnalysisBasisHtml";
import { buildCostAnalysisReportHtml } from "../../utils/costAnalysisReportHtml";
import { API_BASE_URL, authFetch } from "../../config/api";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const readJsonResponse = async (res) => {
  const raw = await res.text();
  try {
    return JSON.parse(raw);
  } catch {
    const preview = raw.trim().slice(0, 120);
    throw new Error(
      preview.startsWith("<!DOCTYPE") || preview.startsWith("<html")
        ? "API returned HTML instead of JSON. Check the backend URL or auth session."
        : "API returned an unexpected non-JSON response."
    );
  }
};

const getJobOrder = (source) => {
  const candidates = [
    source?.job_order,
    source?.job_order_no,
    source?.job_order_number,
    source?.jo_no,
    source?.jo_number,
    source?.jobOrder,
    source?.jobOrderNo,
    source?.jo,
    source?.request?.job_order,
    source?.request?.job_order_no,
    source?.request?.job_order_number,
    source?.request?.jobOrder,
    source?.request?.jobOrderNo,
    source?.cost_analysis?.job_order,
    source?.cost_analysis?.job_order_no,
    source?.cost_analysis_report?.job_order,
    source?.cost_analysis_report?.job_order_no,
    source?.cost_analysis_report?.job_order_number,
    source?.cost_analysis_report?.jobOrder,
    source?.report?.job_order,
  ];

  return candidates.find((value) => String(value || "").trim())?.toString().trim() || "";
};

const matchesRequest = (left, right) => {
  const leftId = left?.request_id || left?.request?.id || left?.id;
  const rightId = right?.request_id || right?.request?.id || right?.id;
  const leftRfp = left?.rfp_no || left?.request?.rfp_no;
  const rightRfp = right?.rfp_no || right?.request?.rfp_no;
  return (
    (leftId && rightId && String(leftId) === String(rightId)) ||
    (leftRfp && rightRfp && String(leftRfp) === String(rightRfp))
  );
};

const CostAnalysisUser127Approvals = () => {
  const user = useSelector((state) => state.auth.user);
  const currentUserId = Number(user?.user_id || user?.id || 0);
  const isSuperadmin = String(user?.role || "").toLowerCase() === "superadmin";
  const canApprove = currentUserId === 127 || isSuperadmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [savingId, setSavingId] = useState("");
  const [activeRow, setActiveRow] = useState(null);
  const [relatedRequests, setRelatedRequests] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectReason, setShowRejectReason] = useState(false);
  const previewScrollRef = useRef(null);
  const [shipmentDetails, setShipmentDetails] = useState(null);
  const [activePreviewKey, setActivePreviewKey] = useState("cost-analysis");
  const [previewZoom, setPreviewZoom] = useState(1);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    if (activeRow?.id) {
      setActivePreviewKey("cost-analysis");
      setPreviewZoom(1);
      setRejectReason("");
      setShowRejectReason(false);
    }
  }, [activeRow?.id]);

  const loadRows = async () => {
    setLoading(true);
    setError("");
    try {
      const url = new URL(`${API_BASE_URL}/api/accounting/cost-analysis`);
      url.searchParams.set("scope", "final_approver");
      url.searchParams.set("summary", "1");
      const res = await authFetch(url, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (!json.success) {
        throw new Error(json.message || "Failed to load cost analysis approvals");
      }
      setRows(json.data || []);
    } catch (err) {
      setError(err.message || "Failed to load cost analysis approvals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  useEffect(() => {
    const loadShipment = async () => {
      const jobOrder = getJobOrder(activeRow);
      if (activeRow?.shipment_details) {
        setShipmentDetails(activeRow.shipment_details);
        return;
      }
      if (!jobOrder) {
        setShipmentDetails(null);
        return;
      }

      try {
        const url = new URL(`${API_BASE_URL}/api/accounting/shipments`);
        url.searchParams.set("q", jobOrder);
        url.searchParams.set("limit", 1);
        const res = await authFetch(url, { credentials: "include" });
        const json = await readJsonResponse(res);
        setShipmentDetails(json.success && json.data?.length ? json.data[0] : null);
      } catch (err) {
        console.error("shipment lookup", err);
        setShipmentDetails(null);
      }
    };

    loadShipment();
  }, [
    activeRow?.job_order,
    activeRow?.job_order_no,
    activeRow?.jo_no,
    activeRow?.jobOrder,
    activeRow?.jo,
    activeRow?.shipment_details,
  ]);

  const pendingRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (rows || [])
      .filter((row) => {
        const stage = String(row.cost_analysis_stage || "").toLowerCase();
        return ["submitted", "partially_approved", "verified", "approved"].includes(stage) &&
          (statusFilter === "all" || (statusFilter === "partially_approved" && ["partially_approved", "verified"].includes(stage)) || statusFilter === stage);
      })
      .filter((row) => {
        if (!term) return true;
        const report = row.cost_analysis_report || {};
        return [
          row.rfp_no,
          row.payee,
          row.job_order,
          report.team_leader_name,
          report.consignee,
          row.cost_analysis_summary,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      })
      .sort((a, b) => {
        const aDate = new Date(a.cost_analysis?.verified_at || a.updated_at || 0).getTime();
        const bDate = new Date(b.cost_analysis?.verified_at || b.updated_at || 0).getTime();
        return bDate - aDate;
      });
  }, [rows, search, statusFilter]);

  const statusCounts = useMemo(() => {
    return (rows || []).reduce((counts, row) => {
      const stage = String(row.cost_analysis_stage || "").toLowerCase();
      if (stage === "submitted") counts.submitted += 1;
      if (["partially_approved", "verified"].includes(stage)) counts.partially_approved += 1;
      if (["approved", "completed"].includes(stage)) counts.approved += 1;
      return counts;
    }, { submitted: 0, partially_approved: 0, approved: 0 });
  }, [rows]);

  const activeRowStage = String(activeRow?.cost_analysis_stage || "").toLowerCase();
  const canApproveActiveRow = canApprove && ["submitted", "partially_approved", "verified"].includes(activeRowStage);
  const activeRowSaving = Boolean(activeRow && (savingId === String(activeRow.id) || savingId === `reject-${activeRow.id}`));
  const activeChanges = Array.isArray(activeRow?.cost_analysis?.changes_summary)
    ? activeRow.cost_analysis.changes_summary
    : [];

  const downloadCostAnalysis = async () => {
    if (!activeRow?.id || downloadLoading) return;

    setDownloadLoading(true);
    setError("");
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/accounting/cost-analysis/${activeRow.id}/report/pdf`,
        { credentials: "include" }
      );

      if (!res.ok) {
        const json = await readJsonResponse(res).catch(() => null);
        throw new Error(json?.message || "Failed to download cost analysis report");
      }

      const blob = await res.blob();
      const fileName = `cost-analysis-${activeRow.rfp_no || activeRow.job_order || activeRow.id}.pdf`.replace(
        /[^a-zA-Z0-9._-]+/g,
        "_"
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (err) {
      setError(err.message || "Failed to download cost analysis report");
    } finally {
      setDownloadLoading(false);
    }
  };

  const approveRow = async (row) => {
    if (!row?.id || !canApprove) return;

    setSavingId(String(row.id));
    setError("");
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/${row.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "approve" }),
      });
      const json = await readJsonResponse(res);
      if (!json.success) {
        throw new Error(json.message || "Failed to approve cost analysis");
      }

      setRows((current) => current.filter((item) => String(item.id) !== String(row.id)));
      setActiveRow(null);
    } catch (err) {
      setError(err.message || "Failed to approve cost analysis");
    } finally {
      setSavingId("");
    }
  };

  const rejectRow = async (row) => {
    if (!row?.id || !canApprove) return;

    const reason = rejectReason.trim();
    if (!reason) {
      setError("Please provide a reason for rejection.");
      return;
    }

    setSavingId(`reject-${row.id}`);
    setError("");
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/${row.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "reject", reason }),
      });
      const json = await readJsonResponse(res);
      if (!res.ok || !json.success) {
        throw new Error(json.message || "Failed to reject cost analysis");
      }

      setRows((current) => current.filter((item) => String(item.id) !== String(row.id)));
      setRejectReason("");
      setShowRejectReason(false);
      setActiveRow(null);
    } catch (err) {
      setError(err.message || "Failed to reject cost analysis");
    } finally {
      setSavingId("");
    }
  };

  const openReview = async (row) => {
    setActiveRow(row);
    setRelatedRequests([]);
    setReviewLoading(true);
    setError("");
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/${row.id}`, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (!res.ok || !json.success) {
        throw new Error(json.message || "Failed to load cost analysis details");
      }
      const detail = json.data || row;
      const jobOrder = getJobOrder(detail) || getJobOrder(row);
      let requests = [];

      if (jobOrder) {
        try {
          const joRes = await authFetch(
            `${API_BASE_URL}/api/accounting/jo-ledger/${encodeURIComponent(jobOrder)}`,
            { credentials: "include" }
          );
          const joJson = await readJsonResponse(joRes);
          const joRequests = joJson.data?.requests || joJson.data?.data?.requests;
          if (joRes.ok && joJson.success && Array.isArray(joRequests)) {
            requests = joRequests;
          }
        } catch (joErr) {
          console.error("JO preview lookup", joErr);
        }
      }

      const matchedRequest = requests.find((item) => matchesRequest(item, detail));
      const merged = { ...(matchedRequest || {}), ...detail };
      setActiveRow((current) => (current && String(current.id) === String(row.id) ? merged : current));
      setRelatedRequests(
        requests.length
          ? requests.map((item) => matchesRequest(item, detail) ? { ...item, ...merged } : item)
          : [merged]
      );
    } catch (err) {
      setError(err.message || "Failed to load cost analysis details");
    } finally {
      setReviewLoading(false);
    }
  };

  const previewRequests = useMemo(() => {
    if (!activeRow) return relatedRequests;
    if (!relatedRequests.length) return [activeRow];
    return relatedRequests.some((item) => matchesRequest(item, activeRow))
      ? relatedRequests.map((item) => matchesRequest(item, activeRow) ? { ...item, ...activeRow } : item)
      : [...relatedRequests, activeRow];
  }, [relatedRequests, activeRow]);

  const previewHtml = useMemo(() => {
    if (!activeRow) return "";

    const report = activeRow.cost_analysis_report || {};
    const billingRows = report.billing_charge_rows || [];
    const receiptedRows = report.receipted_charge_rows || [];
    const actualRows = report.actual_cost_rows || [];
    const billingSubtotal =
      report.billing_charges_subtotal ||
      billingRows.reduce((sum, row) => sum + Number(row.computed_amount || row.base_amount || 0), 0);
    const receiptedSubtotal =
      report.receipted_charges_subtotal ||
      receiptedRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const grandTotalValue = report.grand_total || billingSubtotal + receiptedSubtotal;
    const actualCostTotalValue =
      report.actual_cost_total ||
      actualRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const grossProfitValue = report.gross_profit || grandTotalValue - actualCostTotalValue;

    return buildCostAnalysisReportHtml({
      request: activeRow,
      shipment: activeRow.shipment_details || shipmentDetails,
      reportData: report,
      billingRows,
      receiptedRows,
      actualRows,
      billingSubtotal,
      receiptedSubtotal,
      grandTotalValue,
      actualCostTotalValue,
      grossProfitValue,
      connectedRequests: previewRequests,
    });
  }, [activeRow, previewRequests, shipmentDetails]);

  const previewDocuments = [
    ...previewRequests.map((request, index) => ({
      key: `request-${request.id || index}`,
      label: `Request · ${request.rfp_no || index + 1}`,
      description: request.payee || "Cash voucher basis",
      html: buildRequestVoucherHtml({
        item: request,
        shipment: request.shipment_details || shipmentDetails,
      }),
    })),
    ...previewRequests.flatMap((request, index) => {
      const report = request?.liquidation_report || request?.liquidation?.report;
      if (!report) return [];
      return [{
        key: `liquidation-${request.id || index}`,
        label: `Liquidation · ${request.rfp_no || index + 1}`,
        description: "Liquidation report basis",
        html: buildLiquidationDocumentHtml({
          request,
          reportData: report,
          shipment: request.shipment_details || shipmentDetails,
          reportDateCreatedValue: report.report_date_created || request.created_at,
          dockValue: report.dock || "",
          poNoValue: report.po_no || "",
          referenceNoValue: report.reference_no || "",
          deliveryDateValue: report.delivery_date || "",
          actualRows: report.actual_cost_rows || [],
          advanceRowsValue: report.advance_rows || [],
          actualCostTotalValue: report.total_actual_cost_incurred || 0,
          advancesTotalValue: report.total_advances || 0,
          reimbursementTotalValue: report.total_reimbursement_return || 0,
        }),
      }];
    }),
    {
      key: "cost-analysis",
      label: "Cost Analysis",
      description: "Full report output",
      html: previewHtml,
    },
  ];
  const activePreview = previewDocuments.find((document) => document.key === activePreviewKey) || previewDocuments[previewDocuments.length - 1];
  const liquidationDocumentCount = previewDocuments.filter((document) =>
    document.key.startsWith("liquidation-")
  ).length;

  return (
    <div className="min-h-full bg-slate-50 p-6 lg:p-8 font-poppins">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-[2rem] border border-white/60 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Cost Analysis
              </p>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">
                Cost Analysis for Approval
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600">
                Final approval for reports that have received initial approval from User 130.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[{ key: "submitted", label: "Submitted", color: "text-sky-700", bg: "bg-sky-50" }, { key: "partially_approved", label: "Partially Approved", color: "text-violet-700", bg: "bg-violet-50" }, { key: "approved", label: "Approved", color: "text-emerald-700", bg: "bg-emerald-50" }].map((card) => (
                <button type="button" key={card.key} onClick={() => setStatusFilter(statusFilter === card.key ? "all" : card.key)} className={`rounded-2xl border px-4 py-3 text-left transition ${card.bg} ${statusFilter === card.key ? "border-blue-400 ring-2 ring-blue-100" : "border-slate-100 hover:border-slate-300"}`}>
                  <div className="text-xs uppercase tracking-[0.12em] text-slate-500">{card.label}</div>
                  <div className={`mt-1 text-2xl font-bold ${card.color}`}>{statusCounts[card.key]}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-md">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search request, payee, JO, or team leader..."
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm outline-none ring-0 transition focus:border-blue-400"
              />
            </div>
            <button
              type="button"
              onClick={loadRows}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <i className="fa-solid fa-rotate-right" />
              Refresh
            </button>
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        <div className="rounded-[2rem] border border-white/60 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Partially approved reports</h2>
              <p className="text-sm text-slate-600">
                Approve partially approved cost analyses and finalize the report signature.
              </p>
            </div>
            <div className="text-sm text-slate-500">
              {pendingRows.length} item{pendingRows.length === 1 ? "" : "s"}
            </div>
          </div>

          {loading ? (
            <div className="px-6 py-12 text-center text-sm text-slate-500">
              Loading pending approvals...
            </div>
          ) : pendingRows.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                    <th className="px-6 py-4">RFP No</th>
                    <th className="px-6 py-4">Payee</th>
                    <th className="px-6 py-4">Job Order</th>
                    <th className="px-6 py-4">Team Leader</th>
                    <th className="px-6 py-4">Preparer</th>
                    <th className="px-6 py-4 text-right">Status</th>
                    <th className="px-6 py-4">Initial Approval</th>
                    <th className="px-6 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {pendingRows.map((row) => {
                    const report = row.cost_analysis_report || {};
                    return (
                      <tr
                        key={row.id}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => openReview(row)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openReview(row);
                          }
                        }}
                      >
                        <td className="px-6 py-4 font-semibold text-blue-700">{row.rfp_no || "—"}</td>
                        <td className="px-6 py-4 text-slate-900">{row.jo_payee || "—"}</td>
                        <td className="px-6 py-4 text-slate-600">{row.job_order || "—"}</td>
                        <td className="px-6 py-4 text-slate-700">
                          <div className="font-medium">
                            {report.team_leader_name || row.cost_analysis?.team_leader_name || "—"}
                          </div>
                          <div className="text-xs text-slate-500">
                            {Number(row.cost_analysis?.team_leader_user_id || report.team_leader_user_id || 0)
                              ? `User ID ${Number(row.cost_analysis?.team_leader_user_id || report.team_leader_user_id)}`
                              : "No team leader selected"}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {report.preparer_name || "—"}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="inline-flex rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-200">
                            {String(row.cost_analysis_stage || "").toLowerCase() === "submitted"
                              ? "Submitted"
                              : ["partially_approved", "verified"].includes(String(row.cost_analysis_stage || "").toLowerCase())
                                ? "Partially Approved"
                                : "Approved"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {formatDateTime(row.cost_analysis?.verified_at || row.cost_analysis?.submitted_at || row.updated_at)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openReview(row);
                            }}
                            className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-semibold text-blue-700 shadow-sm transition hover:bg-blue-100"
                          >
                            <i className="fa-solid fa-eye" />
                            Review
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center text-sm text-slate-500">
              No cost analysis reports are waiting for user 127 approval.
            </div>
          )}
        </div>
      </div>

      {activeRow
        ? ReactDOM.createPortal(
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/55 px-4">
              <div className="absolute inset-0" role="presentation" onClick={() => !activeRowSaving && setActiveRow(null)} />
              <div className="relative z-10 flex h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
                <div className="shrink-0 border-b border-slate-800/20 bg-slate-900 px-6 py-5 text-white">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                        Operational Review
                      </p>
                      <h3 className="mt-1 text-xl font-bold text-white">
                        Cost Analysis
                      </h3>
                      <p className="mt-1 text-sm text-slate-300">
                        {activeRow.rfp_no || "—"} | {activeRow.jo_payee || "—"} | {activeRow.job_order || "No job order"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => !activeRowSaving && setActiveRow(null)}
                      className="rounded-xl p-2 text-slate-300 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label="Close modal"
                      disabled={activeRowSaving}
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                  <div className="mt-3 rounded-2xl border border-white/10 bg-white/10 px-3 py-2 text-sm text-slate-200">
                    <div className="font-semibold text-white">
                      {canApproveActiveRow ? "Ready for approval" : "Approval disabled for this account"}
                    </div>
                    <div className="mt-1 text-xs text-slate-300">
                      User 127 may approve this report directly, or finalize it after User 130 gives initial approval.
                    </div>
                  </div>
                  {activeChanges.length ? (
                    <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                      <div className="flex items-center gap-2 font-semibold">
                        <i className="fa-solid fa-clock-rotate-left" />
                        Changes requiring re-approval
                      </div>
                      <p className="mt-1 text-xs text-amber-800">
                        Revision {activeRow.cost_analysis?.revision_no || 2} was edited by {activeRow.cost_analysis?.last_edited_by_name || "the creator"} on {formatDateTime(activeRow.cost_analysis?.last_edited_at)}.
                      </p>
                      <div className="mt-3 space-y-2">
                        {activeChanges.map((change, index) => (
                          <div key={`${change.label}-${index}`} className="rounded-xl border border-amber-200 bg-white/70 px-3 py-2">
                            <div className="font-semibold">{change.label}</div>
                            <div className="mt-1 grid gap-1 text-xs sm:grid-cols-2">
                              <span>Before: {change.before}</span>
                              <span>After: {change.after}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto bg-slate-100 p-6">
                  <div className="space-y-4 rounded-[1.75rem] border border-slate-200 bg-white p-4 shadow-sm">
                    {reviewLoading ? (
                      <div className="flex items-center gap-2 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                        <i className="fa-solid fa-spinner fa-spin" />
                        Loading the latest approval details…
                      </div>
                    ) : null}
                    <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
                      <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Preview Docs
                          </div>
                      <div className="mt-1 text-sm text-slate-600">
                        View the generated documents for this Job Order.
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {previewRequests.length} request{previewRequests.length === 1 ? "" : "s"} · {liquidationDocumentCount} liquidation{liquidationDocumentCount === 1 ? "" : "s"} · 1 cost analysis
                      </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {previewDocuments.map((document) => {
                            const isDisabled = !document.html;
                            const isActive = activePreview?.key === document.key;
                            return (
                              <button
                                key={document.key}
                                type="button"
                                disabled={isDisabled}
                                onClick={() => {
                                  setActivePreviewKey(document.key);
                                  setPreviewZoom(1);
                                }}
                                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                                  isActive
                                    ? "bg-blue text-white shadow-sm"
                                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                } ${isDisabled ? "cursor-not-allowed opacity-50" : ""}`}
                              >
                                {document.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 px-1">
                      <span className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white">
                        {activeRow.rfp_no || "Document Preview"}
                      </span>
                      <div className="flex items-center gap-2">
                        {activePreviewKey === "cost-analysis" ? (
                          <button
                            type="button"
                            onClick={downloadCostAnalysis}
                            disabled={downloadLoading || !activePreview?.html}
                            className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 shadow-sm transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <i className={`fa-solid ${downloadLoading ? "fa-spinner fa-spin" : "fa-download"}`} />
                            {downloadLoading ? "Downloading..." : "Download CA"}
                          </button>
                        ) : null}
                        <div className="inline-flex items-center overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                          <button
                            type="button"
                            onClick={() => setPreviewZoom((value) => Math.max(0.8, Number((value - 0.1).toFixed(1))))}
                            className="px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                            aria-label="Zoom out"
                          >
                            −
                          </button>
                          <span className="min-w-[64px] border-x border-slate-200 px-2 py-2 text-center text-xs font-semibold text-slate-600">
                            {Math.round(previewZoom * 100)}%
                          </span>
                          <button
                            type="button"
                            onClick={() => setPreviewZoom((value) => Math.min(1.8, Number((value + 0.1).toFixed(1))))}
                            className="px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                            aria-label="Zoom in"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                    <section className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm">
                      {activePreview.html ? (
                        <div ref={previewScrollRef} className="flex h-[68vh] min-h-[520px] items-start justify-center overflow-auto bg-slate-100 p-4">
                          <iframe
                            title={`${activePreview.label} preview`}
                            srcDoc={activePreview.html}
                            className="h-[64vh] min-h-[500px] w-full shrink-0 border-0 bg-white shadow-sm"
                            style={{
                              transform: `scale(${previewZoom})`,
                              transformOrigin: "center top",
                            }}
                          />
                        </div>
                      ) : (
                        <div className="flex h-[70vh] min-h-[560px] items-center justify-center px-6 text-sm text-slate-500">
                          No liquidation report found yet.
                        </div>
                      )}
                    </section>
                  </div>
                </div>

                <div className="shrink-0 border-t border-slate-100 bg-white px-5 py-3">
                  {!canApprove ? (
                    <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      You are signed in as user ID {currentUserId || "—"}. Only user 127 can give final approval.
                    </div>
                  ) : null}
                  {canApproveActiveRow && showRejectReason ? (
                    <div className="mb-2 flex items-end gap-3 rounded-2xl border border-red-200 bg-red-50 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <label htmlFor="cost-analysis-reject-reason" className="block text-xs font-bold uppercase tracking-[0.12em] text-red-800">
                        Return / rejection remarks <span className="text-red-600">*</span>
                        </label>
                        <textarea
                          id="cost-analysis-reject-reason"
                          value={rejectReason}
                          onChange={(event) => setRejectReason(event.target.value)}
                          rows={1}
                          placeholder="Describe what needs to be corrected before resubmission..."
                          className="mt-1 w-full resize-none rounded-xl border border-red-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-100"
                        />
                      </div>
                      <p className="hidden shrink-0 text-xs text-red-700 xl:block">
                        Creator can edit and resubmit.
                      </p>
                    </div>
                  ) : null}
                  {error ? (
                    <div className="mb-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {error}
                    </div>
                  ) : null}
                  <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={() => setActiveRow(null)}
                      disabled={activeRowSaving}
                      className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!showRejectReason) {
                          setShowRejectReason(true);
                          previewScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                          return;
                        }
                        rejectRow(activeRow);
                      }}
                      disabled={!canApproveActiveRow || activeRowSaving}
                      className="inline-flex min-w-[180px] items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-5 py-3 text-sm font-semibold text-red-700 shadow-sm transition hover:bg-red-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                    >
                      <i className="fa-solid fa-rotate-left" />
                      {savingId === `reject-${activeRow.id}`
                        ? "Returning..."
                        : showRejectReason
                          ? "Reject & Return"
                          : "Return / Reject"}
                    </button>
                    <button
                      type="button"
                      onClick={() => approveRow(activeRow)}
                      disabled={!canApproveActiveRow || activeRowSaving}
                      className="inline-flex min-w-[180px] items-center justify-center gap-2 rounded-2xl border px-5 py-3 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: canApproveActiveRow ? "#1d4ed8" : "#cbd5e1",
                        borderColor: canApproveActiveRow ? "#2563eb" : "#94a3b8",
                        color: "#ffffff",
                        boxShadow: canApproveActiveRow ? "0 6px 18px rgba(30, 64, 175, 0.18)" : "none",
                      }}
                    >
                      <i className="fa-solid fa-check" />
                      {savingId === String(activeRow.id)
                        ? "Approving..."
                        : activeRowStage === "submitted"
                          ? "Approve Directly"
                          : "Approve Report"}
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default CostAnalysisUser127Approvals;
